import React from "react";
import { motion } from "framer-motion";
import { Clock, TrendingUp, ShieldCheck, MapPin } from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/Reveal";
import { staggerParent, fadeUp, viewportOnce } from "@/lib/anim";

const REASONS = [
    {
        icon: Clock,
        title: "Risparmio di tempo",
        desc: "Deleghi tutto a un unico interlocutore. Zero incombenze quotidiane, zero telefonate agli ospiti.",
    },
    {
        icon: TrendingUp,
        title: "Ottimizzazione del guadagno",
        desc: "Pricing dinamico, annunci curati e alta occupazione per far rendere al massimo il tuo immobile.",
    },
    {
        icon: ShieldCheck,
        title: "Zero stress burocratico",
        desc: "CIR/CIN, questura, SCIA e fisco gestiti da noi, sempre in regola e senza pensieri.",
    },
    {
        icon: MapPin,
        title: "Referenza locale a Roma",
        desc: "Conosciamo la città, gli ospiti e il mercato. Presenza reale sul territorio, sempre.",
    },
];

const STATS = [
    { value: "82%", label: "occupazione media" },
    { value: "9", label: "strutture gestite" },
    { value: "24/7", label: "assistenza ospiti" },
    { value: "100%", label: "qualità interna FaMa Lab" },
];

export default function WhyChoose() {
    return (
        <section
            id="perche-noi"
            className="relative scroll-mt-24 bg-[var(--fama-surface)] py-24 lg:py-32"
            data-testid="why-choose-section"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-end">
                    <div className="lg:col-span-8">
                        <FadeUp>
                            <span className="font-body text-xs font-semibold uppercase tracking-[0.28em] text-[var(--fama-gold)]">
                                Perché scegliere FaMa
                            </span>
                        </FadeUp>
                        <MaskedLines
                            as="h2"
                            lines={["I motivi per affidarci", "il tuo immobile."]}
                            className="mt-5 font-serif-display text-4xl leading-[1.02] tracking-[-0.01em] text-[var(--fama-ink)] sm:text-5xl lg:text-6xl"
                        />
                    </div>
                    <FadeUp delay={0.1} className="lg:col-span-4">
                        <p className="font-body text-base leading-relaxed text-[var(--fama-ink-soft)]">
                            Numeri che raccontano cura, presenza sul territorio e
                            risultati concreti per i nostri proprietari.
                        </p>
                    </FadeUp>
                </div>

                <motion.div
                    variants={staggerParent(0.1)}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
                >
                    {REASONS.map((r, i) => {
                        const Icon = r.icon;
                        return (
                            <motion.div
                                key={r.title}
                                variants={fadeUp}
                                data-testid={`reason-card-${i}`}
                                className="group border-t border-[var(--fama-ink)]/15 pt-6"
                            >
                                <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-full border border-[var(--fama-gold)] text-[var(--fama-gold)] transition-colors duration-500 group-hover:bg-[var(--fama-gold)] group-hover:text-white">
                                    <Icon size={22} strokeWidth={1.4} />
                                </span>
                                <h3 className="font-serif-display text-2xl leading-tight text-[var(--fama-ink)]">
                                    {r.title}
                                </h3>
                                <p className="mt-3 font-body text-sm leading-relaxed text-[var(--fama-ink-soft)]">
                                    {r.desc}
                                </p>
                            </motion.div>
                        );
                    })}
                </motion.div>

                {/* Stats strip */}
                <motion.div
                    variants={staggerParent(0.08)}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-[2px] border border-[var(--fama-border)] bg-[var(--fama-border)] lg:grid-cols-4"
                >
                    {STATS.map((s, i) => (
                        <motion.div
                            key={i}
                            variants={fadeUp}
                            data-testid={`stat-${i}`}
                            className="bg-[var(--fama-bg)] px-6 py-10 text-center"
                        >
                            <p className="font-serif-display text-5xl leading-none text-[var(--fama-gold)] sm:text-6xl">
                                {s.value}
                            </p>
                            <p className="mt-3 font-body text-xs uppercase tracking-[0.18em] text-[var(--fama-ink-soft)]">
                                {s.label}
                            </p>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
